/**
 * ProposalDetailsModal.jsx
 *
 * Affiche le détail complet d'une proposition (annonce colis + trajet
 * concernés, message, prix proposé, profils) avant que le destinataire
 * accepte ou refuse — ou avant que l'auteur retire sa proposition.
 *
 * Toutes les données proviennent de l'objet `p` déjà enrichi par
 * `_enrich_proposition` côté backend (GET /propositions/received|sent) :
 *   p.shipment   → doc shipment complet (notes_private, description, photos...)
 *   p.trip       → doc trip complet (description, price_per_kg...)
 *   p.from_user  → auteur de la proposition
 *   p.to_user    → destinataire
 *
 * Aucun fetch supplémentaire : tout est déjà en mémoire dans PropositionsPage.
 *
 * Réutilise listingParsers.js (source unique de vérité) pour décoder
 * notes_private / description, exactement comme AnnoncesPage (QV modal).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Check, X, Undo2, ArrowRight, Clock, Lock, Zap, MessageCircle } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';
import {
  CONSTRAINT_META, CARRIER_PREF_META, CONDITION_LABELS, TRANSPORT_LABELS, ccySym,
} from '@/utils/listingParsers';
import { cityFlag as flag } from '@/utils/cityFlag';
import { rateDateLabel } from '@/utils/currencyUtils';
import VerificationBadges from '@/components/VerificationBadges';

const fmtPrice = (amount, currency) => `${Number(amount ?? 0).toFixed(0)} ${ccySym(currency || 'EUR')}`;
const fmtConverted = (amount, currency) => {
  const n = Number(amount ?? 0);
  return `${n >= 10 ? n.toFixed(0) : n.toFixed(2)} ${ccySym(currency || 'EUR')}`;
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '·';
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '·';
const initials = (u) => `${(u?.first_name?.[0] || '?')}${(u?.last_name?.[0] || '')}`.toUpperCase();

const STATUS_LABEL = {
  PENDING:   { label: 'En attente', tone: 'pending' },
  ACCEPTED:  { label: 'Acceptée',   tone: 'accepted' },
  REJECTED:  { label: 'Refusée',    tone: 'rejected' },
  WITHDRAWN: { label: 'Retirée',    tone: 'muted' },
  EXPIRED:   { label: 'Expirée',    tone: 'muted' },
};

/**
 * Bloc "annonce" générique — affiche shipment OU trip avec ses infos
 * parsées, dans le même esprit que le QuickView modal d'AnnoncesPage,
 * mais condensé pour tenir dans une colonne du modal proposition.
 *
 * IMPORTANT (Règle des Hooks) : tous les useMemo sont appelés
 * inconditionnellement, à chaque rendu, AVANT le early-return
 * `if (!listing)`. Ils sont rendus tolérants à `listing` null/undefined
 * via l'opérateur optionnel — les parseurs gèrent déjà `undefined` et
 * renvoient {} dans ce cas.
 */
function ListingSummary({ kind, listing, label }) {
  const isShipment = kind === 'colis';

  // Catégorie colis : v2 champ structuré, sinon regex v1
  const pkgCategory = useMemo(() => {
    if (!isShipment) return '';
    if (listing?.categories?.length > 0) {
      const raw = listing.categories;
      const parts = raw.length > 1 ? raw.filter(c => c !== 'Autre') : [...raw];
      return parts.join(', ');
    }
    const m = (listing?.description || '').match(/Cat[ée]gorie\s*:?\s*([^·\n]+)/i);
    return m ? m[1].trim() : '';
  }, [isShipment, listing?.categories, listing?.description]);

  // Description : le texte libre de l'auteur (N4 : plus d'ancien format encodé dedans)
  const descriptionText = (listing?.description || '').trim();

  if (!listing) {
    return (
      <div className="pdm-listing pdm-listing--empty">
        <div className="pdm-listing-label">{label}</div>
        <div className="pdm-hint">Annonce introuvable (peut-être supprimée).</div>
      </div>
    );
  }

  const route = `${listing.departure_city || '?'} → ${listing.arrival_city || '?'}`;
  const dateLabel = isShipment ? 'Date butoire' : 'Date de départ';
  const priceVal = isShipment ? listing.price_offered : listing.price_per_kg;
  const priceUnit = isShipment
    ? ccySym(listing.primary_currency || 'EUR')
    : `${ccySym(listing.primary_currency || 'EUR')}/kg`;
  const weightVal = isShipment ? listing.weight_kg : listing.available_weight_kg;
  const weightLabel = isShipment ? 'Poids' : 'Capacité dispo';

  // Champs structurés du colis
  const shipConstraints    = Array.isArray(listing.constraints) ? listing.constraints : [];
  const shipConstraintCustom = listing.constraint_custom || '';
  const shipCarrierPrefs   = Array.isArray(listing.carrier_preferences) ? listing.carrier_preferences : [];
  const shipMsg            = listing.message_to_carriers || '';
  const isUrgent           = shipConstraints.includes('urgent');

  // Champs structurés du trajet
  const tripTransport = listing.transport_modes?.length > 0
    ? listing.transport_modes.map(t => TRANSPORT_LABELS[t] || t).join(', ')
    : '·';
  const tripCategories = (() => {
    const raw = listing.accepted_categories || [];
    return raw.length > 1 ? raw.filter(c => c !== 'Autre') : raw;
  })();
  const tripConditions = Array.isArray(listing.conditions) ? listing.conditions : [];
  const isTripFirm  = listing.counter_offers === false;
  const tripMsg     = listing.message_to_senders || '';
  const tripMaxPkg  = listing.max_weight_per_parcel;

  return (
    <div className={`pdm-listing pdm-listing--${kind}`}>
      <div className="pdm-listing-label">
        {label}
        <span className={`pdm-type-tag pdm-type-tag--${kind}`}>
          {isShipment ? '📦 Colis' : '✈️ Trajet'}
        </span>
      </div>

      <div className="pdm-route">{flag(listing.departure_city)} {listing.departure_city || '?'} → {flag(listing.arrival_city)} {listing.arrival_city || '?'}</div>

      <div className="pdm-mini-grid">
        <div className="pdm-mini-cell">
          <div className="pdm-mini-label">{dateLabel}</div>
          <div className="pdm-mini-value">{fmtDate(listing.departure_date)}</div>
        </div>
        <div className="pdm-mini-cell">
          <div className="pdm-mini-label">{weightLabel}</div>
          <div className="pdm-mini-value">{weightVal ?? '·'} kg</div>
        </div>
        <div className="pdm-mini-cell">
          <div className="pdm-mini-label">{isShipment ? 'Budget proposé' : 'Tarif'}</div>
          <div className="pdm-mini-value pdm-mini-value--accent">{priceVal ?? '·'} {priceUnit}</div>
        </div>
        <div className="pdm-mini-cell">
          <div className="pdm-mini-label">{isShipment ? 'Type de colis' : 'Transport'}</div>
          <div className="pdm-mini-value">{isShipment ? (pkgCategory || '·') : tripTransport}</div>
        </div>
        {isShipment && listing.dimensions && (
          <div className="pdm-mini-cell">
            <div className="pdm-mini-label">Dimensions</div>
            <div className="pdm-mini-value">{listing.dimensions}</div>
          </div>
        )}
        {!isShipment && listing.max_dimensions && (
          <div className="pdm-mini-cell">
            <div className="pdm-mini-label">Dim. max</div>
            <div className="pdm-mini-value">{listing.max_dimensions}</div>
          </div>
        )}
        {!isShipment && tripMaxPkg != null && (
          <div className="pdm-mini-cell">
            <div className="pdm-mini-label">Max / colis</div>
            <div className="pdm-mini-value">{tripMaxPkg} kg</div>
          </div>
        )}
      </div>

      {descriptionText && (
        <div className="pdm-desc">
          <div className="pdm-mini-label">Description</div>
          <p>{descriptionText}</p>
        </div>
      )}

      {/* ── Sections COLIS v2 : contraintes / préférences / urgence ── */}
      {isShipment && isUrgent && (
        <div className="pdm-tags-row" style={{ marginBottom: 6 }}>
          <span className="pdm-tag pdm-tag--urgent">🚀 Urgent</span>
        </div>
      )}

      {isShipment && (shipConstraints.length > 0 || shipConstraintCustom) && (
        <div className="pdm-tagblock">
          <div className="pdm-mini-label">Contraintes de transport</div>
          <div className="pdm-tags-row">
            {shipConstraints.map((cid) => {
              const m = CONSTRAINT_META[cid];
              return m ? <span key={cid} className="pdm-tag">{m.icon} {m.label}</span> : null;
            })}
            {shipConstraintCustom && <span className="pdm-tag">📋 {shipConstraintCustom}</span>}
          </div>
        </div>
      )}

      {isShipment && shipCarrierPrefs.length > 0 && (
        <div className="pdm-tagblock">
          <div className="pdm-mini-label">Préférences transporteur</div>
          <div className="pdm-tags-row">
            {shipCarrierPrefs.map((id) => {
              const m = CARRIER_PREF_META[id];
              return m ? <span key={id} className="pdm-tag pdm-tag--teal">{m.icon} {m.label}</span> : null;
            })}
          </div>
        </div>
      )}

      {isShipment && shipMsg && (
        <div className="pdm-author-msg">« {shipMsg} »</div>
      )}

      {/* ── Sections TRAJET v2 : catégories / conditions ── */}
      {!isShipment && tripCategories.length > 0 && (
        <div className="pdm-tagblock">
          <div className="pdm-mini-label">Catégories acceptées</div>
          <div className="pdm-tags-row">
            {tripCategories.map((c) => (
              <span key={c} className="pdm-tag">{c}</span>
            ))}
          </div>
        </div>
      )}

      {!isShipment && (tripConditions.length > 0 || isTripFirm) && (
        <div className="pdm-tagblock">
          <div className="pdm-mini-label">Conditions d'acceptation</div>
          <div className="pdm-tags-row">
            {tripConditions.map((c) => (
              <span key={c} className="pdm-tag pdm-tag--teal">{CONDITION_LABELS[c] || c}</span>
            ))}
            {isTripFirm && <span className="pdm-tag">🔒 Prix ferme</span>}
          </div>
        </div>
      )}

      {!isShipment && tripMsg && (
        <div className="pdm-author-msg">« {tripMsg} »</div>
      )}
    </div>
  );
}

function ProposalStepper({ p }) {
  const deadline = p.is_direct ? (p.response_deadline || p.expires_at) : p.expires_at;

  const typeLabel = p.payment_reserved
    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Lock size={12} strokeWidth={2} /> Séquestre · 7 jours</span>
    : p.is_direct
    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Zap size={12} strokeWidth={2} /> Proposition directe · 12h</span>
    : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MessageCircle size={12} strokeWidth={2} /> Négociation · 3 jours</span>;

  const remaining = (() => {
    if (p.status !== 'PENDING' || !deadline) return null;
    const diff = new Date(deadline) - new Date();
    if (diff <= 0) return null;
    const totalMin = Math.floor(diff / 60000);
    const h = Math.floor(totalMin / 60);
    const min = totalMin % 60;
    if (h < 24) return min > 0 ? `${h}h ${min}min restantes` : `${h}h restantes`;
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return rh > 0 ? `${d}j ${rh}h restants` : `${d}j restants`;
  })();

  const finalStep = {
    PENDING:   { dot: 'wait',  title: 'En attente de réponse', sub: deadline ? `Expire le ${fmtDateTime(deadline)}` : null },
    ACCEPTED:  { dot: 'ok',    title: 'Proposition acceptée',  sub: p.updated_at ? `Le ${fmtDateTime(p.updated_at)}` : null },
    REJECTED:  { dot: 'ko',    title: 'Proposition refusée',   sub: p.updated_at ? `Le ${fmtDateTime(p.updated_at)}` : null },
    WITHDRAWN: { dot: 'muted', title: 'Proposition retirée',   sub: p.updated_at ? `Le ${fmtDateTime(p.updated_at)}` : null },
    EXPIRED:   { dot: 'muted', title: 'Proposition expirée',   sub: p.updated_at ? `Le ${fmtDateTime(p.updated_at)}` : null },
  }[p.status] || { dot: 'wait', title: p.status, sub: null };

  const IconCheck = () => <Check size={11} strokeWidth={2.5} aria-hidden="true" />;
  const IconX     = () => <X     size={11} strokeWidth={2.5} aria-hidden="true" />;
  const IconClock = () => <Clock size={11} strokeWidth={2}   aria-hidden="true" />;

  const step2Done = p.status !== 'PENDING';

  return (
    <div className="pdm-stepper">
      {/* Étape 1 : Envoyée */}
      <div className="pdm-step">
        <div className="pdm-step-col">
          <div className="pdm-step-dot pdm-step-dot--done"><IconCheck /></div>
          <div className="pdm-step-line pdm-step-line--gold" />
        </div>
        <div className="pdm-step-body">
          <div className="pdm-step-title">Proposition envoyée</div>
          <div className="pdm-step-sub">{fmtDateTime(p.created_at)}</div>
        </div>
      </div>

      {/* Étape 2 : Délai */}
      <div className="pdm-step">
        <div className="pdm-step-col">
          <div className={`pdm-step-dot pdm-step-dot--${step2Done ? 'done' : 'active'}`}>
            {step2Done ? <IconCheck /> : <IconClock />}
          </div>
          <div className={`pdm-step-line pdm-step-line--${step2Done ? 'gold' : 'teal'}`} />
        </div>
        <div className="pdm-step-body">
          <div className="pdm-step-title">Délai de réponse</div>
          <div className="pdm-step-sub">{typeLabel}</div>
          {remaining && <div className="pdm-step-chip">{remaining}</div>}
        </div>
      </div>

      {/* Étape 3 : Résultat */}
      <div className="pdm-step pdm-step--last">
        <div className="pdm-step-col">
          <div className={`pdm-step-dot pdm-step-dot--${finalStep.dot}`}>
            {finalStep.dot === 'ok'   && <IconCheck />}
            {finalStep.dot === 'ko'   && <IconX />}
            {finalStep.dot === 'wait' && '…'}
            {finalStep.dot === 'muted' && '·'}
          </div>
        </div>
        <div className="pdm-step-body pdm-step-body--last">
          <div className={`pdm-step-title${finalStep.dot === 'wait' ? ' pdm-step-title--muted' : ''}`}>
            {finalStep.title}
          </div>
          {finalStep.sub && <div className="pdm-step-sub">{finalStep.sub}</div>}
        </div>
      </div>
    </div>
  );
}

/**
 * ProposalDetailsModal
 *
 * Props:
 *  - proposition : objet `p` enrichi (shipment, trip, from_user, to_user...)
 *  - tab         : 'received' | 'sent' — détermine qui est "l'autre" et
 *                   quelles actions sont disponibles
 *  - busy        : bool — action en cours (désactive les boutons)
 *  - onClose     : () => void
 *  - onAccept / onReject / onWithdraw : (proposition) => void
 *    (mêmes callbacks que PropositionCard, réutilisés tels quels)
 */
export default function ProposalDetailsModal({ proposition, tab, busy, onClose, onAccept, onReject, onWithdraw }) {
  const [activeTab, setActiveTab] = useState('proposition');
  const [withdrawPending, setWithdrawPending] = useState(false);

  useEffect(() => {
    setActiveTab('proposition');
  }, [proposition?.id]);

  if (!proposition) return null;
  const p = proposition;

  const sLabel = STATUS_LABEL[p.status] || { label: p.status, tone: 'muted' };
  const other = tab === 'received' ? p.from_user : p.to_user;
  const otherRoleHint = tab === 'received'
    ? (p.direction === 'CARRIER_TO_SENDER' ? 'Transporteur' : 'Expéditeur')
    : (p.direction === 'CARRIER_TO_SENDER' ? 'Expéditeur' : 'Transporteur');

  const directionText = p.direction === 'CARRIER_TO_SENDER'
    ? (tab === 'received'
        ? 'Ce membre propose son trajet pour transporter votre colis.'
        : 'Vous proposez votre trajet pour transporter ce colis.')
    : (tab === 'received'
        ? 'Ce membre propose son colis pour votre trajet.'
        : 'Vous proposez votre colis pour ce trajet.');

  // Ordre d'affichage : toujours "Colis" puis "Trajet", peu importe la direction —
  // c'est plus lisible et cohérent avec AnnoncesPage (où colis et trajet sont
  // deux types d'annonces distincts).
  const shipmentLabel = p.direction === 'SENDER_TO_CARRIER'
    ? (tab === 'received' ? "Le colis proposé" : 'Votre colis')
    : (tab === 'received' ? 'Votre annonce colis' : 'Le colis du destinataire');

  const tripLabel = p.direction === 'CARRIER_TO_SENDER'
    ? (tab === 'received' ? 'Le trajet proposé' : 'Votre trajet')
    : (tab === 'received' ? 'Votre trajet' : 'Le trajet du destinataire');

  const canAcceptReject = p.status === 'PENDING' && tab === 'received';
  const canWithdraw = p.status === 'PENDING' && tab === 'sent';
  const canGoToMission = p.status === 'ACCEPTED' && p.mission_id;

  return (
    <div className="pdm-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="proposal-details-modal">
      <div className="pdm-modal">

        {/* Handle */}
        <div className="pdm-handle-wrap" aria-hidden="true"><div className="pdm-handle" /></div>

        {/* ── Header ── */}
        <div className="pdm-head">
          <div className="pdm-head-left">
            <div className="pdm-av" aria-hidden="true">{initials(other)}</div>
            <div>
              <div className="pdm-head-name">
                {other ? `${other.first_name || ''} ${other.last_name || ''}`.trim() : 'Membre'}
                <VerificationBadges user={other} compact />
              </div>
              <div className="pdm-head-sub">{otherRoleHint} · {directionText}</div>
            </div>
          </div>
          <div className="pdm-head-right">
            <span className={`pdm-status pdm-status--${sLabel.tone}`}>{sLabel.label}</span>
            <button type="button" className="pdm-close" onClick={onClose} aria-label="Fermer" data-testid="pdm-close">
              <X size={18} strokeWidth={2.5} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="pdm-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={activeTab === 'proposition'}
            className={`pdm-tab${activeTab === 'proposition' ? ' on' : ''}`}
            onClick={() => setActiveTab('proposition')}>
            Proposition
          </button>
          <button type="button" role="tab" aria-selected={activeTab === 'colis'}
            className={`pdm-tab${activeTab === 'colis' ? ' on' : ''}`}
            onClick={() => setActiveTab('colis')}>
            📦 Colis
          </button>
          <button type="button" role="tab" aria-selected={activeTab === 'trajet'}
            className={`pdm-tab${activeTab === 'trajet' ? ' on' : ''}`}
            onClick={() => setActiveTab('trajet')}>
            ✈️ Trajet
          </button>
        </div>

        {/* ── Body ── */}
        <div className="pdm-body">

          {activeTab === 'proposition' && (
            <>
              <div className="pdm-price-hero" data-testid="pdm-price">
                <div className="pdm-mini-label">Prix proposé</div>
                <div className="pdm-price-hero-amt" data-testid="pdm-price-amount">{fmtPrice(p.proposed_price, p.currency)}</div>
                {p.converted_amount != null && p.converted_currency && p.converted_currency !== p.currency && (
                  <div className="pdm-hint">
                    ≈ {fmtConverted(p.converted_amount, p.converted_currency)}
                    {p.exchange_rate && <span> · taux {Number(p.exchange_rate).toFixed(4)}</span>}
                  </div>
                )}
                {rateDateLabel(p.exchange_rate_date, p.exchange_provider) && (
                  <div className="pdm-hint" style={{ fontSize: 10.5 }} data-testid="pdm-rate-date">
                    Conversion : {rateDateLabel(p.exchange_rate_date, p.exchange_provider)}
                  </div>
                )}
                <div className="pdm-price-hero-date">Envoyée le {fmtDateTime(p.created_at)}</div>
              </div>

              <ProposalStepper p={p} />

              {p.message && (
                <div className="pdm-author-msg pdm-author-msg--proposal">
                  <span className="pdm-mini-label">Message de {other?.first_name || 'l\'auteur'}</span>
                  <p>« {p.message} »</p>
                </div>
              )}

              {p.status === 'REJECTED' && p.reject_reason && (
                <div className="pdm-reject-reason">
                  <strong>Motif du refus&nbsp;:</strong> {p.reject_reason}
                </div>
              )}
            </>
          )}

          {activeTab === 'colis' && (
            <ListingSummary kind="colis" listing={p.shipment} label={shipmentLabel} />
          )}

          {activeTab === 'trajet' && (
            <ListingSummary kind="trajet" listing={p.trip} label={tripLabel} />
          )}

        </div>

        {/* ── Footer : actions contextuelles ── */}
        <div className="pdm-foot">
          {canAcceptReject && (
            <>
              <button type="button" className="pdm-btn pdm-btn-teal" disabled={busy} onClick={() => onAccept(p)} data-testid="pdm-btn-accept">
                <Check size={16} strokeWidth={2.5} aria-hidden="true" /> Accepter la proposition
              </button>
              <button type="button" className="pdm-btn pdm-btn-danger" disabled={busy} onClick={() => onReject(p)} data-testid="pdm-btn-reject">
                <X size={16} strokeWidth={2.5} aria-hidden="true" /> Refuser
              </button>
            </>
          )}
          {canWithdraw && (
            <button type="button" className="pdm-btn pdm-btn-ghost" disabled={busy} onClick={() => setWithdrawPending(true)} data-testid="pdm-btn-withdraw">
              <Undo2 size={16} strokeWidth={2} aria-hidden="true" /> Retirer la proposition
            </button>
          )}
          {canGoToMission && (
            <a className="pdm-btn pdm-btn-primary" href={`/app/missions/${p.mission_id}`} data-testid="pdm-btn-mission">
              <ArrowRight size={16} strokeWidth={2} aria-hidden="true" /> Voir la mission
            </a>
          )}
          {!canAcceptReject && !canWithdraw && !canGoToMission && (
            <button type="button" className="pdm-btn pdm-btn-ghost" onClick={onClose} data-testid="pdm-btn-close-only">
              Fermer
            </button>
          )}
        </div>

      </div>

      <ConfirmDialog
        open={withdrawPending}
        onClose={() => setWithdrawPending(false)}
        onConfirm={() => { setWithdrawPending(false); onWithdraw(p); }}
        variant="destructive"
        icon="ban"
        title="Retirer la proposition ?"
        subtitle="Cette action est irréversible. La proposition sera annulée."
        confirmLabel="Confirmer le retrait"
        busy={busy}
      />
    </div>
  );
}
